import React, { useContext, useEffect } from 'react';
import { Wifi } from 'lucide-react';
import { AssetTabs } from '../../components/assets/AssetTabs';
import { IpAddressesTable } from '../../components/assets/IpAddressesTable';
import { AssetsFilters } from '../../components/assets/AssetsFilters';
import { AssetsMetrics } from '../../components/assets/AssetsMetrics';
import { ExportButton, AddAssetButton } from '../../components/assets/AssetActionButtons';
import { useTeams } from '../../contexts/TeamsContext';
import { AssetsApi, IPAddress, IpAddressesListRequest, Technology } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { AuthContext } from '../../contexts/AuthContext';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import ExportActionBtn from '@/components/ui/ExportActionBtn';

const parseJsonData = (data: string | null): any => {
  try {
    return JSON.parse(data || '{}');
  } catch (error) {
    return {};
  }
};

const IpAddresses = () => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize state from URL search params
  const [currentPage, setCurrentPage] = React.useState(() => {
    const page = searchParams.get('page');
    return page ? parseInt(page, 10) : 1;
  });
  const [searchQuery, setSearchQuery] = React.useState(searchParams.get('search') || '');
  const [selectedType, setSelectedType] = React.useState(searchParams.get('type') || 'all');
  const [selectedStatus, setSelectedStatus] = React.useState(searchParams.get('status') || 'all');
  const [selectedTags, setSelectedTags] = React.useState<string[]>(
    searchParams.getAll('tag') || []
  );
  const [selectedRegions, setSelectedRegions] = React.useState<string[]>(
    searchParams.getAll('region') || []
  );
  // Risk score filter states
  const [selectedRiskScoreMin, setSelectedRiskScoreMin] = React.useState<string>(
    searchParams.get('riskScore') || ''
  );
  const [selectedRiskScoreMax, setSelectedRiskScoreMax] = React.useState<string>(
    searchParams.get('riskScoreMax') || ''
  );
  // Findings count filter states
  const [selectedFindingsMin, setSelectedFindingsMin] = React.useState<number | undefined>(
    searchParams.get('findingsMin') ? parseInt(searchParams.get('findingsMin') || '0', 10) : undefined
  );
  const [selectedFindingsMax, setSelectedFindingsMax] = React.useState<number | undefined>(
    searchParams.get('findingsMax') ? parseInt(searchParams.get('findingsMax') || '0', 10) : undefined
  );
  const [selectedOrdering, setSelectedOrdering] = React.useState<string>(
    searchParams.get('ordering') || 'riskScore'
  );
  const [ipAddresses, setIpAddresses] = React.useState<IPAddress[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = React.useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>({ count: 0, next: null, previous: null });

  // Update local state when search parameters change (for back button navigation)
  useEffect(() => {
    // Only update if the change is from navigation (not from local state changes)
    const pageParam = searchParams.get('page');
    const searchParam = searchParams.get('search');
    const typeParam = searchParams.get('type');
    const statusParam = searchParams.get('status');
    const tagParams = searchParams.getAll('tag');
    const regionParams = searchParams.getAll('region');
    const riskScoreParam = searchParams.get('riskScore');
    const riskScoreMaxParam = searchParams.get('riskScoreMax');
    const findingsMinParam = searchParams.get('findingsMin');
    const findingsMaxParam = searchParams.get('findingsMax');
    const orderingParam = searchParams.get('ordering');
    
    // Update page if it changed
    if (pageParam) {
      const newPage = parseInt(pageParam, 10);
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    } else if (currentPage !== 1) {
      // If page param is removed, reset to page 1
      setCurrentPage(1);
    }
    
    // Update search query if it changed
    if (searchParam !== searchQuery) {
      setSearchQuery(searchParam || '');
    }
    
    // Update type if it changed
    if (typeParam !== selectedType) {
      setSelectedType(typeParam || 'all');
    }
    
    // Update status if it changed
    if (statusParam !== selectedStatus) {
      setSelectedStatus(statusParam || 'all');
    }
    
    // Update tags if they changed
    if (JSON.stringify(tagParams) !== JSON.stringify(selectedTags)) {
      setSelectedTags(tagParams || []);
    }
    
    // Update regions if they changed
    if (JSON.stringify(regionParams) !== JSON.stringify(selectedRegions)) {
      setSelectedRegions(regionParams || []);
    }
    
    // Update risk score min if it changed
    if (riskScoreParam !== selectedRiskScoreMin) {
      setSelectedRiskScoreMin(riskScoreParam || '');
    }
    
    // Update risk score max if it changed
    if (riskScoreMaxParam !== selectedRiskScoreMax) {
      setSelectedRiskScoreMax(riskScoreMaxParam || '');
    }
    
    // Update findings min if it changed
    if (findingsMinParam !== (selectedFindingsMin?.toString() || null)) {
      setSelectedFindingsMin(findingsMinParam ? parseInt(findingsMinParam, 10) : undefined);
    }
    
    // Update findings max if it changed
    if (findingsMaxParam !== (selectedFindingsMax?.toString() || null)) {
      setSelectedFindingsMax(findingsMaxParam ? parseInt(findingsMaxParam, 10) : undefined);
    }
    
    // Update ordering if it changed
    if (orderingParam !== selectedOrdering) {
      setSelectedOrdering(orderingParam || 'riskScore');
    }
  }, [searchParams]);

  const fetchIpAddresses = React.useCallback(async () => {
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new AssetsApi(getApiConfiguration(token));
      
      // Prepare API request with all filters
      const requestParams: IpAddressesListRequest = {
        teamSlug: currentTeam.slug,
        page: currentPage,
        search: searchQuery,
        ordering: selectedOrdering,
      };
      // Add risk score filters if selected
      if (selectedRiskScoreMin && selectedRiskScoreMin !== 'no_filter') {
        requestParams.riskScore = selectedRiskScoreMin;
      }
      // if (selectedRiskScoreMax && selectedRiskScoreMax !== 'no_filter') {
      //   requestParams.riskScoreMax = selectedRiskScoreMax;
      // }
      
      // Add findings count filters if selected
      if (selectedFindingsMin !== undefined && selectedFindingsMin > 0) {
        requestParams.findingsMin = selectedFindingsMin;
      }
      if (selectedFindingsMax !== undefined && selectedFindingsMax > 0) {
        requestParams.findingsMax = selectedFindingsMax;
      }
      const response = await api.ipAddressesList(requestParams);
      setIpAddresses(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      });
    } catch (err) {
      console.error('Error fetching IP addresses:', err);
      setError('Failed to load IP addresses');
    } finally {
      setIsLoading(false);
    }
  }, [
    currentTeam?.slug, 
    token, 
    currentPage, 
    searchQuery, 
    selectedRiskScoreMin, 
    selectedRiskScoreMax, 
    selectedFindingsMin, 
    selectedFindingsMax,
    selectedOrdering,
  ]);

  const fetchAllIpAddresses = async (limit: number) => {
    if (!currentTeam?.slug || !token) return [];
    
    const api = new AssetsApi(getApiConfiguration(token));
    const response = await api.ipAddressesList({
      teamSlug: currentTeam.slug,
      search: searchQuery,
      ordering: selectedOrdering,
      limit
    });
    return response.results;
  };

  React.useEffect(() => {
    fetchIpAddresses();
  }, [fetchIpAddresses]);

  // Helper function to build URL with search parameters
  const buildUrlWithParams = (params: Record<string, any>) => {
    const urlParams = new URLSearchParams();
    
    // Add page parameter if greater than 1
    if (params.page && params.page > 1) {
      urlParams.set('page', params.page.toString());
    }
    
    // Add search query if present
    if (params.search) {
      urlParams.set('search', params.search);
    }
    
    // Add type if not 'all'
    if (params.type && params.type !== 'all') {
      urlParams.set('type', params.type);
    }
    
    // Add status if not 'all'
    if (params.status && params.status !== 'all') {
      urlParams.set('status', params.status);
    }
    
    // Add tags if present
    if (params.tags && params.tags.length > 0) {
      params.tags.forEach((tag: string) => urlParams.append('tag', tag));
    }
    
    // Add regions if present
    if (params.regions && params.regions.length > 0) {
      params.regions.forEach((region: string) => urlParams.append('region', region));
    }
    
    // Add risk score filter if present
    if (params.riskScore && params.riskScore !== '') {
      urlParams.set('riskScore', params.riskScore);
    }
    
    // Add findings count filters if present
    if (params.findingsMin !== undefined && params.findingsMin > 0) {
      urlParams.set('findingsMin', params.findingsMin.toString());
    }
    if (params.findingsMax !== undefined && params.findingsMax > 0) {
      urlParams.set('findingsMax', params.findingsMax.toString());
    }
    
    // Add ordering if present
    if (params.ordering && params.ordering !== 'riskScore') {
      urlParams.set('ordering', params.ordering);
    }
    
    return `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
  };

  // Handle page change with URL param update
  const handlePageChange = (page: number) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page,
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { pageChange: true, timestamp: Date.now() }
    });
    
    // Update the local state
    setCurrentPage(page);
  };

  // Handle search change with URL param update
  const handleSearchChange = (query: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on search
      search: query,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { searchChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Handle type change
  const handleTypeChange = (type: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedType(type);
    setCurrentPage(1);
  };

  // Handle status change
  const handleStatusChange = (status: string) => {
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  // Handle tag change
  const handleTagChange = (tag: string, checked: boolean) => {
    const newTags = checked 
      ? [...selectedTags, tag] 
      : selectedTags.filter(t => t !== tag);
      
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: newTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedTags(newTags);
    setCurrentPage(1);
  };

  // Handle region change
  const handleRegionChange = (region: string, checked: boolean) => {
    const newRegions = checked 
      ? [...selectedRegions, region] 
      : selectedRegions.filter(r => r !== region);
      
    // Create URL with updated parameters
    const url = buildUrlWithParams({
      page: 1, // Reset to first page on filter change
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: newRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(url, {
      state: { filterChange: true, timestamp: Date.now() }
    });
    
    // Update local state
    setSelectedRegions(newRegions);
    setCurrentPage(1);
  };

  // Handle risk score min changes
  const handleRiskScoreMinChange = (value: string) => {
    setSelectedRiskScoreMin(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: value,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle findings min changes
  const handleFindingsMinChange = (value: number | undefined) => {
    setSelectedFindingsMin(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: value,
      findingsMax: selectedFindingsMax,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle findings max changes
  const handleFindingsMaxChange = (value: number | undefined) => {
    setSelectedFindingsMax(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: value,
      ordering: selectedOrdering
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle ordering changes
  const handleOrderingChange = (value: string) => {
    setSelectedOrdering(value);
    
    // Reset to page 1 when filter changes
    setCurrentPage(1);
    
    // Construct the URL with updated parameters
    const newUrl = buildUrlWithParams({
      page: 1, // Explicitly set page to 1
      search: searchQuery,
      type: selectedType,
      status: selectedStatus,
      tags: selectedTags,
      regions: selectedRegions,
      riskScore: selectedRiskScoreMin,
      findingsMin: selectedFindingsMin,
      findingsMax: selectedFindingsMax,
      ordering: value
    });
    
    // Navigate to the new URL
    navigate(newUrl, { state: { timestamp: new Date().getTime() } });
  };

  // Handle filter reset
  const handleReset = () => {
    // Navigate to base URL without parameters
    navigate(location.pathname, {
      state: { reset: true, timestamp: Date.now() }
    });
    
    // Reset all local state
    setSearchQuery('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedTags([]);
    setSelectedRegions([]);
    setCurrentPage(1);
  };

  // Transform API data for the table
  const displayIpAddresses = ipAddresses.length > 0 ? ipAddresses : [
    {
      id: 1,
      ipAddress: '192.168.1.100',
      hostname: 'web-server-prod.example.com',
      isActive: true,
      riskScore: 'C',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date('2024-02-01'),
      tags: ['production', 'web-server', 'nginx'],
      technologies: [{ id: 1, name: 'nginx', version: '1.20.1', icon: '/icons/nginx.svg', color: '#009639' }],
      country: 'United States',
      city: 'San Francisco',
      region: 'California',
      postal: '94105',
      timezone: 'America/Los_Angeles',
      loc: '37.7749,-122.4194',
      org: 'Amazon.com, Inc.',
      asn: 'AS16509',
      cdn: true,
      cdnName: 'CloudFront',
      waf: true,
      wafName: 'AWS WAF',
      openPorts: [
        { port: 80, protocol: 'tcp', service: 'http', state: 'open' },
        { port: 443, protocol: 'tcp', service: 'https', state: 'open' },
        { port: 22, protocol: 'tcp', service: 'ssh', state: 'filtered' }
      ],
      team: 1
    }
  ];

  const transformedIpAddresses = displayIpAddresses.map(ip => {
    // Parse JSON strings into objects
    const technologies = typeof ip.technologies === 'string' ? parseJsonData(ip.technologies) : ip.technologies;
    
    // Handle ports data - check if it exists in the API response
    let portsData: any[] = [];
    if ('ports' in ip) {
      portsData = typeof ip.ports === 'string' ? parseJsonData(ip.ports) : [];
    }
    
    return {
      id: ip.id.toString(),
      ipAddress: ip.ipAddress,
      hostname: ip.hostname,
      isActive: ip.isActive === undefined ? false : ip.isActive, // Ensure isActive is always a boolean
      riskScore: (ip.riskScore || 'n/a').toUpperCase(),
      updatedAt: new Date(ip.updatedAt).toLocaleString(),
      createdAt: new Date(ip.createdAt).toLocaleString(),
      tags: ip.tags || [],
      technologies: technologies?.map((tech: any) => ({
        name: tech.name || 'Unknown',
        version: tech.version || 'Unknown',
        logo_path: tech.logo_path || '',
        color: tech.color || 'gray'
      })) || [],
      location: {
        country: ip.country,
        city: ip.city,
        region: ip.region,
        postal: ip.postal,
        timezone: ip.timezone,
        coordinates: ip.loc
      },
      network: {
        org: ip.org,
        asn: ip.asn,
        cdn: ip.cdn,
        cdnName: ip.cdnName,
        waf: ip.waf,
        wafName: ip.wafName
      },
      openPorts: portsData?.map((port: any) => ({
        port: port.number,
        protocol: port.protocol,
        service: port.service,
        state: port.state
      })) || []
    };
  });

  // Metrics for IP addresses page
  const ipMetrics = {
    web: 0,
    servers: 0,
    databases: 0,
    network: ipAddresses.length,
    vulnerable: ipAddresses.filter(ip => !ip.isActive).length,
    secure: ipAddresses.filter(ip => ip.isActive).length,
    total: ipAddresses.length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Wifi className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">IP Addresses</h1>
            <p className="text-sm text-gray-400">Track and manage your IP address assets</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <ExportActionBtn
            fetchAllData={() => fetchAllIpAddresses(paginationInfo.count)}
            filename="ip_addresses"
            disabled={ipAddresses?.length === 0}
          />
          <AddAssetButton onClick={() => console.log('Adding new IP address...')} />
        </div>
      </div>

      {/* Metrics Overview 
      <AssetsMetrics metrics={ipMetrics} />
*/}
      {/* Tabs */}
      <AssetTabs />

      {/* Filters */}
      <AssetsFilters
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        selectedType={selectedType}
        onTypeChange={handleTypeChange}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedTags={selectedTags}
        onTagChange={handleTagChange}
        selectedRegions={selectedRegions}
        onRegionChange={handleRegionChange}
        selectedRiskScoreMin={selectedRiskScoreMin}
        onRiskScoreMinChange={handleRiskScoreMinChange}
        selectedFindingsMin={selectedFindingsMin}
        onFindingsMinChange={handleFindingsMinChange}
        selectedFindingsMax={selectedFindingsMax}
        onFindingsMaxChange={handleFindingsMaxChange}
        selectedOrdering={selectedOrdering}
        onOrderingChange={handleOrderingChange}
        onReset={handleReset}
        onExport={() => {
          console.log('Exporting IP addresses...');
        }}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading IP addresses</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <IpAddressesTable
          ipAddresses={transformedIpAddresses}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          totalIpAddresses={paginationInfo.count}
          itemsPerPage={10}
          paginationInfo={paginationInfo}
        />
      )}
    </div>
  );
};

export default IpAddresses;